import { useEffect, useState } from "react";
import { useUser } from "../provider/UserProvider";
import { ThemeData, getCurrentTheme } from "./themeUtils";

export const useTheme = () => {
  const { theme, updateTheme, resetUserTheme, loading } = useUser();
  const [currentTheme, setCurrentTheme] = useState<ThemeData | null>(null);
  const [isThemeReady, setIsThemeReady] = useState(false);

  useEffect(() => {
    if (!loading) {
      // Get current theme from CSS variables
      const themeFromCSS = getCurrentTheme();
      setCurrentTheme(themeFromCSS);
      setIsThemeReady(true);
    }
  }, [loading, theme]);

  // Listen for theme changes
  useEffect(() => {
    const handleThemeChange = () => {
      const newTheme = getCurrentTheme();
      setCurrentTheme(newTheme);
      setIsThemeReady(true);
    };

    window.addEventListener("themeChanged", handleThemeChange);
    return () => window.removeEventListener("themeChanged", handleThemeChange);
  }, []);

  // Helper function to get primary color with robust fallback
  const getPrimaryColor = (): string => {
    // If theme is not ready, return default
    if (!isThemeReady) {
      return "#1D2B56";
    }

    // Try to get from current theme state
    if (currentTheme?.primary && currentTheme.primary.trim() !== "") {
      return currentTheme.primary;
    }

    // Fallback to CSS variable
    try {
      const root = document.documentElement;
      const cssPrimary = getComputedStyle(root)
        .getPropertyValue("--primary-color")
        .trim();
      if (cssPrimary && cssPrimary !== "") {
        return cssPrimary;
      }
    } catch (error) {
      console.warn("Error getting CSS variable --primary-color:", error);
    }

    // Final fallback
    return "#1D2B56";
  };

  // Helper function to get primary light color with robust fallback
  const getPrimaryLightColor = (): string => {
    // If theme is not ready, return default
    if (!isThemeReady) {
      return "#2a3a6a";
    }

    // Try to get from current theme state
    if (currentTheme?.primaryLight && currentTheme.primaryLight.trim() !== "") {
      return currentTheme.primaryLight;
    }

    // Fallback to CSS variable
    try {
      const root = document.documentElement;
      const cssPrimaryLight = getComputedStyle(root)
        .getPropertyValue("--primary-light")
        .trim();
      if (cssPrimaryLight && cssPrimaryLight !== "") {
        return cssPrimaryLight;
      }
    } catch (error) {
      console.warn("Error getting CSS variable --primary-light:", error);
    }

    // Final fallback
    return "#2a3a6a";
  };

  // Helper function to get primary dark color with robust fallback
  const getPrimaryDarkColor = (): string => {
    // If theme is not ready, return default
    if (!isThemeReady) {
      return "#162142";
    }

    // Try to get from current theme state
    if (currentTheme?.primaryDark && currentTheme.primaryDark.trim() !== "") {
      return currentTheme.primaryDark;
    }

    // Fallback to CSS variable
    try {
      const root = document.documentElement;
      const cssPrimaryDark = getComputedStyle(root)
        .getPropertyValue("--primary-dark")
        .trim();
      if (cssPrimaryDark && cssPrimaryDark !== "") {
        return cssPrimaryDark;
      }
    } catch (error) {
      console.warn("Error getting CSS variable --primary-dark:", error);
    }

    // Final fallback
    return "#162142";
  };

  // Debug function to test theme values
  const debugTheme = () => {
    console.log("Theme Ready:", isThemeReady);
    console.log("Current Theme State:", currentTheme);
    console.log("Primary Color:", getPrimaryColor());
    console.log("Primary Light Color:", getPrimaryLightColor());
    console.log("Primary Dark Color:", getPrimaryDarkColor());
  };

  // Call debug function once on mount
  useEffect(() => {
    debugTheme();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isThemeReady]);

  return {
    theme: currentTheme,
    updateTheme,
    resetUserTheme,
    loading: loading || !isThemeReady,
    getPrimaryColor,
    getPrimaryLightColor,
    getPrimaryDarkColor,
  };
};
